import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Box, Paper, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { useRef, useState } from "react";
import "swiper/css";
import { Swiper, SwiperSlide } from "swiper/react";

const MessageBubble = styled(Paper)(
  ({ theme, isUser, isError, isLoading, hasOnlyImages }) => ({
    marginVertical: 2,
    padding: hasOnlyImages ? 0 : isLoading ? 0 : "8px 12px",
    borderRadius: hasOnlyImages ? 0 : 18,
    alignSelf: isUser ? "flex-end" : "flex-start",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "75%",
      marginLeft: isUser ? "auto" : 0,
      marginRight: isUser ? 0 : "auto",
    },
    [theme.breakpoints.up("sm")]: {
      maxWidth: "60%",
      marginLeft: isUser ? "40%" : 0,
      marginRight: isUser ? 0 : "40%",
    },
    [theme.breakpoints.up("md")]: {
      maxWidth: "50%",
      marginLeft: isUser ? "50%" : 0,
      marginRight: isUser ? 0 : "50%",
    },
    [theme.breakpoints.up("lg")]: {
      maxWidth: "40%",
      marginLeft: isUser ? "60%" : 0,
      marginRight: isUser ? 0 : "60%",
    },
    backgroundColor: hasOnlyImages
      ? "transparent"
      : isUser
      ? "#FF3B30"
      : isError
      ? "#3B1415"
      : "#1C1C1E",
    color: "#FFFFFF",
    marginBottom: 8,
    minHeight: hasOnlyImages ? "auto" : 36,
    width: "fit-content",
    boxShadow: hasOnlyImages ? "none" : undefined,
  })
);

const CarouselContainer = styled(Box)(() => ({
  position: "relative",
  width: "100%",
  marginBottom: 8,
}));

const CarouselPips = styled(Box)(() => ({
  display: "flex",
  justifyContent: "center",
  gap: 8,
  marginTop: 8,
  marginBottom: 8,
}));

const Pip = styled(Box)(({ active }) => ({
  width: 8,
  height: 8,
  borderRadius: "50%",
  backgroundColor: active ? "#FF3B30" : "#38383A",
  transition: "background-color 0.3s ease",
}));

const NavigationArrows = styled(Box)({
  display: "flex",
  gap: "16px",
  color: "#FF3B30",
  "@media (max-width: 600px)": {
    display: "none",
  },
});

const AdminMessageGroupCarousel = ({
  messageId,
  messageGroups,
  metadata,
  timestamp,
  copiedMessageIndices,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const swiperRef = useRef(null);

  if (
    !messageGroups ||
    !Array.isArray(messageGroups) ||
    messageGroups.length === 0
  ) {
    return null;
  }

  const formatTimestamp = (isoTimestamp) => {
    if (!isoTimestamp) return "";
    try {
      const date = new Date(isoTimestamp);
      // Format: MM/DD/YY HH:MM AM/PM
      const formattedDate = date.toLocaleDateString(undefined, {
        month: "2-digit",
        day: "2-digit",
        year: "2-digit",
      });
      const formattedTime = date.toLocaleTimeString(undefined, {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
      return `${formattedDate} ${formattedTime}`;
    } catch (error) {
      console.error("Error formatting timestamp:", error);
      return ""; // Or return the original timestamp or a placeholder
    }
  };

  return (
    <CarouselContainer>
      <MessageBubble
        isUser={false}
        sx={{
          width: "100%",
          padding: 0,
          overflow: "hidden",
          position: "relative",
        }}
      >
        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 2,
              pt: 1.5,
            }}
          >
            <Typography
              sx={{
                color: "#8D8D8D",
                fontSize: "14px",
                fontFamily: "Instrument Sans",
                fontWeight: 600,
                textTransform: "uppercase",
              }}
            >
              {(metadata?.more_suggestions ? "More " : "") +
                (metadata?.activity_type === "reply"
                  ? "Reply Suggestions"
                  : metadata?.activity_type === "initiate"
                  ? "Openers"
                  : "Suggestions") +
                (timestamp ? ` - ${formatTimestamp(timestamp)}` : "")}
            </Typography>
            <NavigationArrows>
              <Box
                onClick={() => swiperRef.current?.slidePrev()}
                sx={{
                  cursor: activeStep > 0 ? "pointer" : "default",
                  opacity: activeStep > 0 ? 1 : 0.5,
                }}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
                </svg>
              </Box>
              <Box
                onClick={() => swiperRef.current?.slideNext()}
                sx={{
                  cursor:
                    activeStep < messageGroups.length - 1
                      ? "pointer"
                      : "default",
                  opacity: activeStep < messageGroups.length - 1 ? 1 : 0.5,
                }}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
                </svg>
              </Box>
            </NavigationArrows>
          </Box>

          {metadata?.activity_type !== "initiate" && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                height: "24px",
                px: 2,
                pb: { xs: 0.5, sm: 1 },
                mt: 1,
              }}
            >
              <Box
                sx={{
                  width: "65px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Typography
                  sx={{
                    color: "#8D8D8D",
                    fontSize: "13px",
                    fontFamily: "Instrument Sans",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    lineHeight: 1,
                  }}
                >
                  Option {activeStep + 1}
                </Typography>
              </Box>
              <Box
                sx={{ display: "flex", alignItems: "center", height: "13px" }}
              ></Box>
            </Box>
          )}
        </Box>
        <Box sx={{ mt: 2, width: "100%", height: "100%" }}>
          <Swiper
            onSwiper={(swiper) => {
              swiperRef.current = swiper;
            }}
            onSlideChange={(swiper) => setActiveStep(swiper.activeIndex)}
            spaceBetween={30}
            slidesPerView={1}
            style={{
              padding: "0 0px",
              marginBottom: 0,
              width: "100%",
              height: "100%",
            }}
          >
            {messageGroups.map((group, groupIndex) => (
              <SwiperSlide
                key={`${messageId}-${groupIndex}`}
                style={{
                  paddingBottom: 0,
                  height: "auto",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    px: 0.5,
                    py: 1,
                    width: "100%",
                    height: "100%",
                  }}
                >
                  <Box sx={{ width: "100%" }}>
                    {Array.isArray(group) &&
                      group.map((text, messageIndex) => (
                        <Box
                          key={`${messageId}-${groupIndex}-${messageIndex}`}
                          sx={{
                            backgroundColor: "#2C2C2E",
                            padding: "8px 8px",
                            borderRadius: 2,
                            mb: messageIndex < group.length - 1 ? 1 : 0,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mr: 1,
                          }}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              sx={{
                                fontSize: {
                                  xs: "17px",
                                  sm: "17px",
                                },
                                lineHeight: {
                                  xs: "22px",
                                  sm: "22px",
                                },
                                fontFamily:
                                  '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", sans-serif',
                                letterSpacing: "-0.022em",
                                fontWeight: 400,
                                wordWrap: "break-word",
                                whiteSpace: "pre-wrap",
                              }}
                            >
                              {text}
                            </Typography>
                          </Box>
                          {copiedMessageIndices?.some(
                            (indices) =>
                              indices.outer === groupIndex &&
                              indices.inner === messageIndex
                          ) && (
                            <Box>
                              <CheckCircleIcon
                                sx={{
                                  fontSize: 14,
                                  color: "#4CAF50",
                                }}
                              />
                            </Box>
                          )}
                        </Box>
                      ))}
                  </Box>
                </Box>
              </SwiperSlide>
            ))}
          </Swiper>
        </Box>
        <CarouselPips>
          {messageGroups.map((_, index) => (
            <Pip key={`${messageId}-${index}`} active={index === activeStep} />
          ))}
        </CarouselPips>
      </MessageBubble>
    </CarouselContainer>
  );
};

export default AdminMessageGroupCarousel;
